import { Controller } from 'stimulus';

// Jobs-index "saved views + editable chips" (design D1).
//
// Filtering is server-truthful: editing a chip mutates the current URL's query
// params and reloads, so the table, chip strip, and dirty save-bar are all
// re-rendered by Rails. This controller only handles the interaction layer
// (chip popovers, the field picker, view tabs/menu, and the save-bar fetches).
export default class extends Controller {
  static targets = ['chipStrip', 'saveBar', 'overflow', 'menu', 'menuDefaultLabel', 'menuPinLabel', 'search'];

  // params that are not user-facing filters (kept across navigations, but never
  // saved into a view or shown as a chip).
  static NON_FILTER = ['saved_search_id', 'view', 'page', 'per', 'utf8', 'authenticity_token', 'commit', 'sort', 'direction'];

  connect() {
    this.catalog = JSON.parse(this.data.get('catalog') || '{"groups":[],"active":[]}');
    this.setDefaultUrl = this.data.get('set-default-url');
    this.countsUrl = this.data.get('counts-url');
    this.pageName = this.data.get('page-name');
    this.defaultViewId = this.data.get('default-view-id');

    this.boundClose = this.closeAllPops.bind(this);
    document.addEventListener('click', this.boundClose);

    // Keep the More-views menu's open direction correct as the bar reflows, so
    // it never overflows the viewport (page horizontal scrollbar) even when the
    // menu is closed — it's only visibility:hidden, so it still takes layout.
    this.boundPosition = this.positionOverflowMenu.bind(this);
    window.addEventListener('resize', this.boundPosition);
    this.positionOverflowMenu();

    this.renderChips();
    this.loadCounts();
    this.restoreSearchFocus();
  }

  /* ============================ Broad search ============================ */
  // Explicit search: Enter or the Search button navigates; the inline ✕ clears.
  searchKeydown(e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    this.runSearch(e.target.value);
  }

  searchClick(e) {
    e.preventDefault();
    this.runSearch(this.searchTarget.value);
  }

  clearSearch(e) {
    e.preventDefault();
    this.runSearch('');
  }

  runSearch(value) {
    // Flag so the cursor returns to the box after the reload (search or clear).
    sessionStorage.setItem('jobsSearchFocus', '1');
    this.applyText('query', (value || '').trim());
  }

  // After a search-driven reload, put the cursor back in the search box.
  restoreSearchFocus() {
    if (!this.hasSearchTarget || sessionStorage.getItem('jobsSearchFocus') === null) return;
    sessionStorage.removeItem('jobsSearchFocus');
    const el = this.searchTarget;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }

  // Tab counts are fetched after paint so they never block the index render.
  // A view that errored server-side comes back null → its badge is dropped.
  loadCounts() {
    if (!this.countsUrl) return;
    fetch(this.countsUrl, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((counts) => {
        this.element.querySelectorAll('[data-view-count]').forEach((el) => {
          const value = counts[el.dataset.viewCount];
          if (value == null) { el.remove(); return; }
          el.textContent = value;
          el.classList.remove('tab-count-loading');
        });
        // Counts change pill widths, which can reflow the bar and move the
        // More-views trigger — recheck the menu's open direction.
        this.positionOverflowMenu();
      })
      .catch(() => {
        this.element.querySelectorAll('.tab-count-loading').forEach((el) => el.remove());
        this.positionOverflowMenu();
      });
  }

  disconnect() {
    document.removeEventListener('click', this.boundClose);
    window.removeEventListener('resize', this.boundPosition);
  }

  /* ============================ URL helpers ============================ */
  currentParams() {
    return new URLSearchParams(window.location.search);
  }

  goWith(params) {
    window.location.href = `${window.location.pathname}?${params.toString()}`;
  }

  applyMulti(key, values) {
    const p = this.currentParams();
    p.delete(key);
    p.delete(`${key}[]`);
    values.forEach((v) => p.append(`${key}[]`, v));
    this.goWith(p);
  }

  applyDate(key, period, start, end) {
    const p = this.currentParams();
    p.delete(key);
    p.delete(`date-start-${key}`);
    p.delete(`date-end-${key}`);
    if (period === 'Custom') {
      p.set(key, 'Custom');
      if (start) p.set(`date-start-${key}`, start);
      if (end) p.set(`date-end-${key}`, end);
    } else if (period) {
      p.set(key, period);
    }
    this.goWith(p);
  }

  applyText(key, text) {
    const p = this.currentParams();
    p.delete(key);
    if (text) p.set(key, text);
    this.goWith(p);
  }

  removeField(field) {
    const p = this.currentParams();
    p.delete(field.key);
    p.delete(`${field.key}[]`);
    if (field.kind === 'date') {
      p.delete(`date-start-${field.key}`);
      p.delete(`date-end-${field.key}`);
    }
    this.goWith(p);
  }

  // The current filter params, shaped for storage on a saved view.
  currentFilterParamsObject() {
    const obj = {};
    for (const [rawKey, value] of this.currentParams().entries()) {
      const isArray = rawKey.endsWith('[]');
      const key = isArray ? rawKey.slice(0, -2) : rawKey;
      if (this.constructor.NON_FILTER.includes(key)) continue;
      if (isArray) {
        (obj[key] = obj[key] || []).push(value);
      } else {
        obj[key] = value;
      }
    }
    return obj;
  }

  fieldByKey(key) {
    for (const group of this.catalog.groups) {
      const found = group.fields.find((f) => f.key === key);
      if (found) return found;
    }
    return null;
  }

  /* ============================ Chips ============================ */
  renderChips() {
    const strip = this.chipStripTarget;
    strip.innerHTML = '';

    this.catalog.active.forEach((chip) => {
      const el = document.createElement('div');
      el.className = 'filter-chip';
      el.innerHTML = `
        <span class="chip-label">${this.escape(chip.label)}:</span>
        <span class="chip-value">${this.escape(chip.display)}</span>
        <i class="fas fa-caret-down chip-caret"></i>
        <span class="chip-remove" title="Remove filter"><i class="fas fa-times text-xs"></i></span>
      `;
      el.addEventListener('click', (e) => {
        if (e.target.closest('.chip-remove')) {
          e.stopPropagation();
          this.removeField(chip);
          return;
        }
        e.stopPropagation();
        this.openValuePopover(el, chip);
      });
      strip.appendChild(el);
    });

    const add = document.createElement('div');
    add.className = 'filter-chip add-filter';
    add.innerHTML = '<i class="fas fa-plus text-xs mr-1"></i>Add filter';
    add.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openFieldPicker(add);
    });
    strip.appendChild(add);

    // Clear All — only when there's at least one chip to clear; sits right
    // after "+ Add filter" so the user can't miss it.
    if (this.catalog.active.length > 0) {
      const clear = document.createElement('a');
      clear.className = 'chip-strip-clear';
      clear.textContent = 'Clear All';
      clear.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.hasSaveBarTarget && !window.confirm('Discard unsaved changes?')) return;
        window.location.href = `${window.location.pathname}?view=all`;
      });
      strip.appendChild(clear);
    }
  }

  // Value popover for a single field. `chip` may be an active chip (carries
  // current values) or a bare catalog field (no values yet).
  openValuePopover(anchor, chip) {
    this.closeAllPops();
    const field = this.fieldByKey(chip.key) || chip;
    const pop = document.createElement('div');
    pop.className = 'chip-popover';

    if (field.kind === 'date') {
      pop.innerHTML = this.datePopoverHtml(field, chip);
    } else if (field.kind === 'text') {
      pop.innerHTML = this.textPopoverHtml(field, chip);
    } else {
      pop.innerHTML = this.multiPopoverHtml(field, chip);
    }

    anchor.appendChild(pop);
    this.openPop = pop;
    pop.addEventListener('click', (e) => e.stopPropagation());
    // Option "open" links (e.g. the Group picker's link to the group page)
    // navigate without toggling the surrounding label's checkbox.
    pop.querySelectorAll('.pop-option-link').forEach((link) => {
      link.addEventListener('click', (e) => e.stopPropagation());
    });
    this.wireValuePopover(pop, field);

    const search = pop.querySelector('.pop-search');
    if (search) {
      search.addEventListener('input', () => {
        const q = search.value.toLowerCase();
        pop.querySelectorAll('.pop-option').forEach((o) => {
          o.style.display = o.textContent.toLowerCase().includes(q) ? '' : 'none';
        });
      });
    }
  }

  multiPopoverHtml(field, chip) {
    const current = chip.values || [];
    const opts = field.options.map((o) => `
      <label class="pop-option">
        <input type="checkbox" data-val="${this.escape(o.value)}" ${current.includes(o.value) ? 'checked' : ''}>
        ${this.escape(o.label)}
        ${o.url ? `<a class="pop-option-link" href="${this.escape(o.url)}">open</a>` : ''}
      </label>`).join('');
    return `
      <div class="pop-header">Filter · ${this.escape(field.label)}</div>
      ${field.options.length > 6 ? '<input class="pop-search" placeholder="Find…">' : ''}
      <div class="pop-options">${opts}</div>
      <div class="pop-actions">
        <a class="text-xs text-gray-500 hover:underline" data-act="clear">Clear</a>
        <button class="text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-50 rounded px-2 py-1" data-act="apply">Apply</button>
      </div>`;
  }

  datePopoverHtml(field, chip) {
    const cur = (chip.values || [])[0] || '';
    const opts = field.options.map((o) =>
      `<option value="${this.escape(o.value)}" ${o.value === cur ? 'selected' : ''}>${this.escape(o.label)}</option>`).join('');
    const custom = cur === 'Custom';
    return `
      <div class="pop-header">Filter · ${this.escape(field.label)}</div>
      <select class="pop-input" data-role="period"><option value="">All</option>${opts}</select>
      <div class="pop-dates" data-role="dates" style="${custom ? '' : 'display:none;'}">
        <div class="w-full mr-1">
          <input type="date" class="pop-input" data-role="start" value="${this.escape(chip.start || '')}">
        </div>
        <div class="w-full ml-1">
          <input type="date" class="pop-input" data-role="end" value="${this.escape(chip.end || '')}">
        </div>
      </div>
      <div class="pop-actions">
        <a class="text-xs text-gray-500 hover:underline" data-act="clear">Clear</a>
        <button class="text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-50 rounded px-2 py-1" data-act="apply">Apply</button>
      </div>`;
  }

  textPopoverHtml(field, chip) {
    const cur = (chip.values || [])[0] || '';
    return `
      <div class="pop-header">Filter · ${this.escape(field.label)} contains</div>
      <input class="pop-input" data-role="text" value="${this.escape(cur)}" placeholder="Contains…">
      <div class="pop-actions">
        <a class="text-xs text-gray-500 hover:underline" data-act="clear">Clear</a>
        <button class="text-xs font-semibold text-blue-700 bg-blue-100 hover:bg-blue-50 rounded px-2 py-1" data-act="apply">Apply</button>
      </div>`;
  }

  wireValuePopover(pop, field) {
    const periodSel = pop.querySelector('[data-role="period"]');
    if (periodSel) {
      periodSel.addEventListener('change', () => {
        const dates = pop.querySelector('[data-role="dates"]');
        if (dates) dates.style.display = periodSel.value === 'Custom' ? 'flex' : 'none';
      });
    }

    pop.querySelector('[data-act="clear"]').addEventListener('click', (e) => {
      e.stopPropagation();
      this.removeField(field);
    });

    pop.querySelector('[data-act="apply"]').addEventListener('click', (e) => {
      e.stopPropagation();
      if (field.kind === 'date') {
        const period = pop.querySelector('[data-role="period"]').value;
        const start = (pop.querySelector('[data-role="start"]') || {}).value;
        const end = (pop.querySelector('[data-role="end"]') || {}).value;
        this.applyDate(field.key, period, start, end);
      } else if (field.kind === 'text') {
        this.applyText(field.key, pop.querySelector('[data-role="text"]').value.trim());
      } else {
        const values = Array.from(pop.querySelectorAll('input[type=checkbox]:checked')).map((c) => c.dataset.val);
        if (values.length === 0) this.removeField(field);
        else this.applyMulti(field.key, values);
      }
    });
  }

  /* ============================ Field picker ============================ */
  openFieldPicker(anchor) {
    this.closeAllPops();
    const activeKeys = this.catalog.active.map((c) => c.key);
    const pop = document.createElement('div');
    pop.className = 'field-picker';

    let html = '<div class="field-picker-search"><input placeholder="Find a field…"></div>';
    this.catalog.groups.forEach((group) => {
      html += `<div class="field-picker-section">${this.escape(group.name)}</div>`;
      group.fields.forEach((f) => {
        const applied = activeKeys.includes(f.key);
        html += `
          <div class="field-picker-row ${applied ? 'applied' : ''}" data-key="${this.escape(f.key)}">
            <span>${this.escape(f.label)}${applied ? ' <span class="text-xs text-gray-400 ml-1">(applied)</span>' : ''}</span>
            <span class="field-meta">${this.escape(f.kind)}</span>
          </div>`;
      });
    });
    pop.innerHTML = html;
    anchor.appendChild(pop);
    this.openPop = pop;
    pop.addEventListener('click', (e) => e.stopPropagation());

    const search = pop.querySelector('.field-picker-search input');
    search.addEventListener('input', () => {
      const q = search.value.toLowerCase();
      pop.querySelectorAll('.field-picker-row').forEach((r) => {
        r.style.display = r.textContent.toLowerCase().includes(q) ? '' : 'none';
      });
    });

    pop.querySelectorAll('.field-picker-row:not(.applied)').forEach((row) => {
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        const field = this.fieldByKey(row.dataset.key);
        if (field) this.openValuePopover(anchor, field);
      });
    });
  }

  /* ============================ Tabs / star / menu ============================ */
  // Whole-tab click: the inner <a class="view-name"> handles modified clicks
  // (cmd/middle/right) natively, so we only navigate when the click landed on
  // dead space (padding, count badge, etc.) and the star/⋯ handlers haven't
  // already stopped propagation.
  openTab(e) {
    if (e.target.closest('a')) return;
    const url = e.currentTarget.dataset.viewUrl;
    if (url) window.location.href = url;
  }

  toggleStar(e) {
    e.stopPropagation();
    const host = e.target.closest('[data-view-id]');
    if (host) this.setDefault(host.dataset.viewId);
  }

  toggleAllJobsDefault(e) {
    e.stopPropagation();
    // Starring "All jobs" just means: clear whatever view is currently default.
    if (this.defaultViewId) this.setDefault(this.defaultViewId);
  }

  // Drawer row pin icon → toggle is_pinned for that view.
  togglePin(e) {
    e.stopPropagation();
    const host = e.target.closest('[data-view-id]');
    if (host) this.togglePinned(host.dataset.viewId);
  }

  setDefault(viewId) {
    if (!viewId) return;
    fetch(`${this.setDefaultUrl}/${viewId}/set_default`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then(() => window.location.reload())
      .catch(() => window.toastr && window.toastr.error('Could not update landing view'));
  }

  // Server enforces "can't unpin the landing view" — surface that as a toast
  // and leave the bar untouched.
  togglePinned(viewId) {
    if (!viewId) return;
    fetch(`${this.setDefaultUrl}/${viewId}/toggle_pinned`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
    })
      .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
      .then(({ ok, body }) => {
        if (!ok) {
          if (window.toastr) window.toastr.warning(body.message || 'Cannot unpin this view.');
          return;
        }
        window.location.reload();
      })
      .catch(() => window.toastr && window.toastr.error('Could not update pin'));
  }

  toggleOverflow(e) {
    e.stopPropagation();
    this.positionOverflowMenu();
    this.overflowTarget.classList.toggle('active');
  }

  // The More-views menu opens rightward by default (left-anchored) to stay clear
  // of the sidebar nav. When the trigger sits near the right edge — e.g. many
  // pinned pills push it over — opening rightward overflows the viewport and adds
  // a page-level horizontal scrollbar. Add `.opens-left` so it opens leftward
  // instead; the trigger is on the right in that case, so the leftward menu still
  // lands inside the content, away from the sidebar. Applied even while closed,
  // since the menu is only visibility:hidden and still contributes to overflow.
  positionOverflowMenu() {
    if (!this.hasOverflowTarget) return;
    const menu = this.overflowTarget.querySelector('.droplist-menu');
    if (!menu) return;
    const triggerLeft = this.overflowTarget.getBoundingClientRect().left;
    const viewport = document.documentElement.clientWidth;
    const overflowsRight = triggerLeft + menu.offsetWidth > viewport - 8;
    this.overflowTarget.classList.toggle('opens-left', overflowsRight);
  }

  openMenu(e) {
    e.stopPropagation();
    this.closeAllPops();
    const pill = e.target.closest('.view-pill');
    this.menuViewId = pill.dataset.viewId;
    this.menuViewName = pill.dataset.viewName;
    this.menuViewParams = pill.dataset.viewParams;
    const isDefault = pill.dataset.viewDefault === 'true';
    const isPinned = pill.dataset.viewPinned === 'true';
    this.menuDefaultLabelTarget.textContent = isDefault ? 'Remove as landing view' : 'Make this my landing view';
    if (this.hasMenuPinLabelTarget) {
      this.menuPinLabelTarget.textContent = isPinned ? 'Unpin from bar' : 'Pin to bar';
    }

    const rect = e.target.closest('.pill-menu').getBoundingClientRect();
    const menu = this.menuTarget;
    menu.style.top = `${rect.bottom + 4}px`;
    menu.style.left = `${Math.max(8, rect.right - 240)}px`;
    menu.classList.add('active');
  }

  menuToggleDefault(e) {
    e.stopPropagation();
    this.setDefault(this.menuViewId);
  }

  menuTogglePin(e) {
    e.stopPropagation();
    this.togglePinned(this.menuViewId);
  }

  menuMoveLeft(e) { e.stopPropagation(); this.moveView('left'); }

  menuMoveRight(e) { e.stopPropagation(); this.moveView('right'); }

  moveView(direction) {
    fetch(`${this.setDefaultUrl}/${this.menuViewId}/move?direction=${direction}`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
    })
      .then(() => window.location.reload())
      .catch(() => { if (window.toastr) window.toastr.error('Could not reorder view'); });
  }

  menuRename(e) {
    e.stopPropagation();
    const name = window.prompt('Rename view:', this.menuViewName);
    if (!name) return;
    fetch(`${this.setDefaultUrl}/${this.menuViewId}`, {
      method: 'PUT',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ saved_search_param: { name } }),
    }).then(() => window.location.reload());
  }

  menuDuplicate(e) {
    e.stopPropagation();
    const params = this.menuViewParams ? JSON.parse(this.menuViewParams) : {};
    this.createView(`${this.menuViewName} (copy)`, params, null);
  }

  menuDelete(e) {
    e.stopPropagation();
    if (!window.confirm(`Delete view "${this.menuViewName}"? This can't be undone.`)) return;
    fetch(`${this.setDefaultUrl}/${this.menuViewId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), Accept: 'application/json' },
    }).then(() => { window.location.href = `${window.location.pathname}?view=all`; });
  }

  newView(e) {
    e.stopPropagation();
    const name = window.prompt('Name your new view:');
    if (!name) return;
    this.createView(name, this.currentFilterParamsObject(), 'open');
  }

  /* ============================ Save bar ============================ */
  saveView(e) {
    e.stopPropagation();
    const bar = this.saveBarTarget;
    fetch(`${bar.dataset.saveUrl}/${bar.dataset.viewId}`, {
      method: 'PUT',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        saved_search_param: {
          name: bar.dataset.viewName,
          page_name: this.pageName,
          params: JSON.stringify(this.currentFilterParamsObject()),
        },
      }),
    }).then(() => {
      if (window.toastr) window.toastr.success(`View "${bar.dataset.viewName}" saved`);
      window.location.reload();
    });
  }

  saveAsNewView(e) {
    e.stopPropagation();
    const name = window.prompt('Name this new view:');
    if (!name) return;
    this.createView(name, this.currentFilterParamsObject(), 'open');
  }

  discardChanges(e) {
    e.stopPropagation();
    const bar = this.saveBarTarget;
    if (bar.dataset.mode === 'system') {
      window.location.href = `${window.location.pathname}?view=all`;
      return;
    }
    // For a saved view, rebuild the URL with the view's stored params merged
    // back in — the controller filters from URL params, so dropping the query
    // string entirely would clear everything (not just the unsaved diff).
    const cleanUrl = bar.dataset.viewCleanUrl;
    if (cleanUrl && cleanUrl !== '') {
      window.location.href = cleanUrl;
    } else {
      window.location.href = `${window.location.pathname}?saved_search_id=${bar.dataset.viewId}`;
    }
  }

  // POST a new saved view. `then`==='open' navigates to it once created.
  createView(name, paramsObj, then) {
    fetch(this.setDefaultUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': Rails.csrfToken(), 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        saved_search_param: {
          name,
          page_name: this.pageName,
          params: JSON.stringify(paramsObj),
        },
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === 'error') {
          if (window.toastr) window.toastr.error('Could not save view');
          return;
        }
        if (then === 'open' && data.id) {
          const p = new URLSearchParams();
          Object.entries(paramsObj).forEach(([k, v]) => {
            if (Array.isArray(v)) v.forEach((x) => p.append(`${k}[]`, x));
            else p.set(k, v);
          });
          p.set('saved_search_id', data.id);
          this.goWith(p);
        } else {
          window.location.reload();
        }
      })
      .catch(() => { if (window.toastr) window.toastr.error('Could not save view'); });
  }

  /* ============================ Misc ============================ */
  closeAllPops() {
    if (this.openPop && this.openPop.parentNode) this.openPop.parentNode.removeChild(this.openPop);
    this.openPop = null;
    if (this.hasOverflowTarget) this.overflowTarget.classList.remove('active');
    if (this.hasMenuTarget) this.menuTarget.classList.remove('active');
  }

  escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
